import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { useLocation, useNavigate } from 'react-router-dom';

const DEFAULT_FILTERS = {
  mainOfficeLocation: '',
  selectedTypes: [],
  industry: '',
  keyword: '',
  onlyWithInvestmentsInfo: false,
};

function normalizeString(value) {
  return typeof value === 'string' ? value : value == null ? '' : String(value);
}

function normalizeSelectedTypes(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeString(item).trim())
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function normalizeFilters(filters = {}) {
  return {
    mainOfficeLocation: normalizeString(filters.mainOfficeLocation || filters.country).trim(),
    selectedTypes: normalizeSelectedTypes(filters.selectedTypes || filters.types),
    industry: normalizeString(filters.industry || filters.industries).trim(),
    keyword: normalizeString(filters.keyword || filters.searchKeyword).trim(),
    onlyWithInvestmentsInfo: Boolean(filters.onlyWithInvestmentsInfo),
  };
}

function getFiltersSignature(filters) {
  return JSON.stringify({
    ...filters,
    selectedTypes: [...filters.selectedTypes].sort(),
  });
}

function areFiltersEqual(left, right) {
  return getFiltersSignature(normalizeFilters(left)) === getFiltersSignature(normalizeFilters(right));
}

function parsePositivePage(value, fallback = 1) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function getRuntimeSearch(fallbackSearch = '') {
  if (typeof window !== 'undefined' && typeof window.location?.search === 'string') {
    return window.location.search;
  }

  return fallbackSearch;
}

function readPageFromSearch(search, pageParam, fallback = 1) {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return parsePositivePage(params.get(pageParam), fallback);
}

export function useInvestorsSearch({
  endpoint,
  pageSize = 10,
  initialPage = 1,
  initialFilters = DEFAULT_FILTERS,
  initialItems = [],
  initialTotalResults = 0,
  initialMeta = {},
  hasInitialResponse = false,
  fetchDebounceMs = 0,
  pageParam = 'page',
  syncPageToUrl = false,
  replaceHistory = true,
  withCredentials = false,
  enabled = true,
  buildRequestParams,
  mapResponse,
  shouldSkipInitialFetch,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const normalizedInitialFilters = useMemo(
    () => normalizeFilters(initialFilters),
    [initialFilters]
  );

  const [filters, setFilters] = useState(normalizedInitialFilters);
  const [currentPage, setCurrentPage] = useState(() => parsePositivePage(initialPage, 1));
  const [items, setItems] = useState(Array.isArray(initialItems) ? initialItems : []);
  const [totalResults, setTotalResults] = useState(Number(initialTotalResults) || 0);
  const [meta, setMeta] = useState(initialMeta || {});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const firstRenderRef = useRef(true);
  const hasInitialResponseRef = useRef(Boolean(hasInitialResponse));
  const hasFetchedOnceRef = useRef(
    Boolean(hasInitialResponse || (Array.isArray(initialItems) && initialItems.length))
  );
  const filtersRef = useRef(normalizedInitialFilters);
  const currentPageRef = useRef(parsePositivePage(initialPage, 1));
  const wasEnabledRef = useRef(Boolean(enabled));
  const activeRequestIdRef = useRef(0);

  const filtersSignature = useMemo(() => getFiltersSignature(filters), [filters]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  const runFetch = useCallback(async () => {
    const requestId = activeRequestIdRef.current + 1;
    activeRequestIdRef.current = requestId;
    setIsLoading(true);
    setError(null);

    try {
      const params = buildRequestParams({
        page: currentPage,
        pageSize,
        filters,
      });

      const { data } = await axios.get(endpoint, {
        params,
        withCredentials,
      });

      if (requestId !== activeRequestIdRef.current) {
        return;
      }

      const next = mapResponse(data);
      hasFetchedOnceRef.current = true;

      startTransition(() => {
        setItems(Array.isArray(next?.items) ? next.items : []);
        setTotalResults(Number(next?.totalResults) || 0);
        setMeta(next?.meta || {});
      });
    } catch (nextError) {
      if (requestId !== activeRequestIdRef.current) {
        return;
      }
      console.error('Error fetching investors:', nextError);
      setError(nextError);
    } finally {
      if (requestId === activeRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    buildRequestParams,
    currentPage,
    endpoint,
    filters,
    mapResponse,
    pageSize,
    withCredentials,
  ]);

  const fetchRef = useRef(runFetch);
  useEffect(() => {
    fetchRef.current = runFetch;
  }, [runFetch]);

  const debouncedFetch = useMemo(() => {
    if (!fetchDebounceMs) {
      return null;
    }

    return debounce(() => fetchRef.current(), fetchDebounceMs);
  }, [fetchDebounceMs]);

  useEffect(() => {
    const isEnabling = !wasEnabledRef.current && enabled;
    wasEnabledRef.current = enabled;

    if (!enabled) {
      return;
    }

    const skipInitialFetch =
      firstRenderRef.current &&
      hasInitialResponseRef.current &&
      typeof shouldSkipInitialFetch === 'function' &&
      shouldSkipInitialFetch({
        page: currentPage,
        filters,
      });

    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      if (skipInitialFetch) {
        return;
      }
    }

    if (isEnabling && hasFetchedOnceRef.current) {
      return;
    }

    if (debouncedFetch) {
      debouncedFetch();
      return;
    }

    fetchRef.current();
  }, [currentPage, debouncedFetch, enabled, filters, filtersSignature, shouldSkipInitialFetch]);

  useEffect(() => {
    return () => {
      debouncedFetch?.cancel();
    };
  }, [debouncedFetch]);

  useEffect(() => {
    if (!enabled || !syncPageToUrl) {
      return;
    }

    const currentSearch = getRuntimeSearch(location.search);
    const params = new URLSearchParams(currentSearch.startsWith('?') ? currentSearch.slice(1) : currentSearch);

    if (currentPage > 1) {
      params.set(pageParam, String(currentPage));
    } else {
      params.delete(pageParam);
    }

    const nextSearch = params.toString();
    const currentNormalized = currentSearch.startsWith('?') ? currentSearch.slice(1) : currentSearch;

    if (nextSearch === currentNormalized) {
      return;
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: replaceHistory }
    );
  }, [
    currentPage,
    location.pathname,
    location.search,
    navigate,
    pageParam,
    replaceHistory,
    syncPageToUrl,
  ]);

  useEffect(() => {
    if (!enabled || !hasFetchedOnceRef.current) {
      return;
    }

    if (meta?.pagingLocked) {
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }

    const safeTotalPages = totalResults > 0
      ? Math.max(1, Math.ceil(totalResults / pageSize))
      : 1;

    if (currentPage > safeTotalPages) {
      setCurrentPage(safeTotalPages);
    }
  }, [currentPage, enabled, meta?.pagingLocked, pageSize, totalResults]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const rawNextPage = readPageFromSearch(location.search, pageParam, 1);
    let nextPage = rawNextPage;

    if (hasFetchedOnceRef.current) {
      if (meta?.pagingLocked) {
        nextPage = 1;
      } else {
        const safeTotalPages = totalResults > 0
          ? Math.max(1, Math.ceil(totalResults / pageSize))
          : 1;
        nextPage = Math.min(rawNextPage, safeTotalPages);
      }
    }

    setCurrentPage((prevPage) => (prevPage === nextPage ? prevPage : nextPage));
  }, [enabled, location.search, meta?.pagingLocked, pageParam, pageSize, totalResults]);

  const applyFilters = useCallback((nextFilters, options = {}) => {
    const { resetPage = true } = options;
    const normalizedNextFilters = normalizeFilters(nextFilters);

    if (areFiltersEqual(filtersRef.current, normalizedNextFilters)) {
      return;
    }

    if (resetPage && currentPageRef.current !== 1) {
      setCurrentPage(1);
    }

    setFilters(normalizedNextFilters);
  }, []);

  const patchFilters = useCallback((patch, options = {}) => {
    const nextFilters = normalizeFilters(
      typeof patch === 'function' ? patch(filtersRef.current) : { ...filtersRef.current, ...patch }
    );

    if (areFiltersEqual(filtersRef.current, nextFilters)) {
      return;
    }

    if (options.resetPage !== false && currentPageRef.current !== 1) {
      setCurrentPage(1);
    }

    setFilters(nextFilters);
  }, []);

  const setPage = useCallback((page) => {
    const nextPage = parsePositivePage(page, 1);
    setCurrentPage((prevPage) => (prevPage === nextPage ? prevPage : nextPage));
  }, []);

  const setKeyword = useCallback((keyword) => {
    patchFilters({ keyword });
  }, [patchFilters]);

  const setMainOfficeLocation = useCallback((mainOfficeLocation) => {
    patchFilters({ mainOfficeLocation });
  }, [patchFilters]);

  const setIndustry = useCallback((industry) => {
    patchFilters({ industry });
  }, [patchFilters]);

  const setSelectedTypes = useCallback((selectedTypes) => {
    patchFilters({ selectedTypes });
  }, [patchFilters]);

  return {
    filters,
    items,
    totalResults,
    isLoading,
    error,
    currentPage,
    setPage,
    applyFilters,
    patchFilters,
    setKeyword,
    setMainOfficeLocation,
    setIndustry,
    setSelectedTypes,
    meta,
  };
}

export default useInvestorsSearch;
